/** Google organic endpoints, read from official vendor documentation on 2026-10-14. */
export const SERP_COMPARISON_FIELDS = [
  ["request", "Request method"],
  ["endpoint", "Google endpoint"],
  ["auth", "Authentication"],
  ["query", "Search query"],
  ["location", "Location"],
  ["language", "Search locale"],
  ["device", "Device"],
  ["pagination", "Pagination"],
  ["output", "Output"],
  ["mode", "Execution"],
] as const
export type SerpField = (typeof SERP_COMPARISON_FIELDS)[number][0]
export type SerpProvider = {
  slug: string
  name: string
  website: string
  pricingUrl: string
  facts: Record<
    SerpField,
    { value: string; sourceUrl: string; checkedAt: string; sourceType: "vendor-docs" }
  >
}
function provider(
  slug: string,
  name: string,
  website: string,
  pricingUrl: string,
  sourceUrl: string,
  values: Record<SerpField, string>,
): SerpProvider {
  return {
    slug,
    name,
    website,
    pricingUrl,
    facts: Object.fromEntries(
      Object.entries(values).map(([key, value]) => [
        key,
        { value, sourceUrl, checkedAt: "2026-10-14", sourceType: "vendor-docs" },
      ]),
    ) as SerpProvider["facts"],
  }
}
export const SERP_PROVIDERS: SerpProvider[] = [
  provider(
    "serpapi",
    "SerpAPI",
    "https://serpapi.com",
    "https://serpapi.com/pricing",
    "https://serpapi.com/search-api",
    {
      request: "GET",
      endpoint: "serpapi.com/search?engine=google",
      auth: "api_key parameter",
      query: "q",
      location: "location / uule, gl",
      language: "hl",
      device: "device",
      pagination: "start (result offset)",
      output: "JSON; HTML output also documented",
      mode: "Synchronous or async=true; no_cache controls cached results",
    },
  ),
  provider(
    "dataforseo",
    "DataForSEO",
    "https://dataforseo.com",
    "https://dataforseo.com/pricing/serp",
    "https://docs.dataforseo.com/v3/serp/google/organic/live/advanced/",
    {
      request: "POST",
      endpoint: "api.dataforseo.com/v3/serp/google/organic/live/advanced",
      auth: "HTTP Basic (API login/password)",
      query: "keyword in JSON task array",
      location: "location_code / location_name / location_coordinate",
      language: "language_code / language_name",
      device: "device",
      pagination: "depth (requested result depth)",
      output: "JSON (Advanced endpoint)",
      mode: "Live synchronous request; separate queued Task API available",
    },
  ),
  provider(
    "searchapi",
    "SearchAPI.io",
    "https://www.searchapi.io",
    "https://www.searchapi.io/#pricing",
    "https://www.searchapi.io/docs/google",
    {
      request: "GET",
      endpoint: "www.searchapi.io/api/v1/search?engine=google",
      auth: "api_key parameter or Bearer header",
      query: "q",
      location: "location / uule, gl",
      language: "hl",
      device: "device",
      pagination: "page (page number)",
      output: "JSON",
      mode: "Synchronous response",
    },
  ),
  provider(
    "valueserp",
    "ValueSERP",
    "https://www.valueserp.com",
    "https://www.valueserp.com/pricing",
    "https://docs.trajectdata.com/valueserp/search-api/searches/google/search",
    {
      request: "GET",
      endpoint: "api.valueserp.com/search",
      auth: "api_key parameter",
      query: "q",
      location: "location, gl; location_auto can set localization",
      language: "hl",
      device: "device",
      pagination: "page / max_page",
      output: "JSON / HTML / CSV",
      mode: "Real-time response; separate batch workflows available",
    },
  ),
]
// Common parameters supply authentication/output evidence for ValueSERP.
for (const key of ["auth", "output", "request", "endpoint"] as const)
  SERP_PROVIDERS[3].facts[key].sourceUrl =
    "https://docs.trajectdata.com/valueserp/search-api/searches/common"

SERP_PROVIDERS[3].facts.device.sourceUrl =
  "https://docs.trajectdata.com/valueserp/search-api/searches/common"
SERP_PROVIDERS[3].facts.mode.sourceUrl =
  "https://docs.trajectdata.com/valueserp/search-api/overview"
